import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import { HTTPService } from "./../../../services/httpService";
import { getDisplayDates } from "../../../util/dateDisplayUtils";
import { convertFromUTC } from "../../../util/convert_timezone";
// Remove the calculation imports - we'll use stored values
// import { calculateFinalAmount, formatCurrency } from './../services/serviceUtils';

import moment from "moment";
import _ from "lodash";

const API = new HTTPService();

export class ViewJob extends Component {
  state = {
    copiedField: null,
  };

  // Utility function for currency formatting (no calculations)
  formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Copy a value to clipboard and show brief feedback
  copyToClipboard = (value, fieldName) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      this.setState({ copiedField: fieldName });
      setTimeout(() => this.setState({ copiedField: null }), 1500);
    }).catch(() => {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = value;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.setState({ copiedField: fieldName });
      setTimeout(() => this.setState({ copiedField: null }), 1500);
    });
  };

  // Fetch Signed URL when the button is clicked (for contract)
  fetchSignedUrl = async () => {
    const newTab = window.open("", "_blank"); // open the tab
    try {
      const response = await API.get(
        `signedURL/?requestId=${this.props.request.requestId}`
      );
      if (response.data.signedUrl) {
        newTab.location.href = response.data.signedUrl; // populate the link
      } else {
        console.error("No signed URL found.");
      }
    } catch (error) {
      console.error("Error fetching signed URL:", error);
      alert("Error accessing contract.");
    }
  };

  // Fetch Estimate PDF URL
  fetchEstimatePdf = async () => {
    try {
      const response = await API.get(`/requests/${this.props.request.requestId}/quote-pdf`);
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        alert('PDF not available at this time.');
      }
    } catch (error) {
      console.error('Error downloading estimate PDF:', error);
      alert('PDF not available at this time.');
    }
  };

  render() {
    let vehicle =
      this.props.request.vehicleYear +
      " " +
      this.props.request.vehicleMake +
      " " +
      this.props.request.vehicleModel +
      (this.props.request.vehicleTrim ? " " + this.props.request.vehicleTrim : "") +
      (this.props.request.vehicleColor ? " - " + this.props.request.vehicleColor : "");

    // Use stored database values - no frontend calculations
    const requestTotal = parseFloat(this.props.request.requestTotal || 0);
    const requestDiscount = parseFloat(this.props.request.requestDiscount || 0);
    const requestTaxes = parseFloat(this.props.request.requestTaxes || 0);
    
    // Customer pays: final amount + taxes (only calculation allowed)
    const finalAmount = requestTotal - requestDiscount;
    const customerTotal = finalAmount + requestTaxes;
    
    let totalCost = this.formatCurrency(customerTotal);
    let totalTax = this.formatCurrency(requestTaxes);
    
    // Use getDisplayDates utility for correct scheduled dates
    const displayDates = getDisplayDates(
      this.props.request,
      this.props.calendarDays || [],
      this.props.userType
    );
    let startDate = moment(displayDates.startDate).format("ddd, MMM Do YYYY");
    let endDate = moment(displayDates.endDate).format("ddd, MMM Do YYYY");
    const startLabel = displayDates.startLabel || "Work Starts";
    const endLabel = displayDates.endLabel || "Work scheduled to complete";

    var noteText = "None";
    if (this.props.notes.length > 0) {
      if (this.props.notes[0].note_text.length > 1)
        noteText = this.props.notes[0].note_text;
    }

    const services = this.props.services || [];

    // Check if any service has materials data
    const hasMaterialsData = services.length > 0 && services.some(s =>
      s.materials && s.materials.length > 0
    );

    // Check if any service has a recommended product
    const hasRecommendedProduct = services.length > 0 && services.some(s =>
      s.recommended_product_id && s.recommended_product_name
    );

    // Show Product column if either materials or recommended products exist
    const showProductColumn = hasMaterialsData || hasRecommendedProduct;

    // Build columns - include Product column if there's data
    const columns = [
      {
        dataField: "desc",
        text: "Service",
      },
      ...(showProductColumn ? [{
        dataField: "productDisplay",
        text: "Product",
        formatter: (cell) => {
          if (!cell || cell === '-') return '-';
          return <span dangerouslySetInnerHTML={{ __html: cell }} />;
        }
      }] : []),
      {
        dataField: "amount",
        text: "Amount",
      },
      {
        dataField: "duration",
        text: "Duration",
      },
    ];

    let services_ = [];
    for (let i = 0; i < services.length; i++) {
      // Use stored service_value (final amount after discount) from database
      // If service_value doesn't exist, fall back to calculation for backward compatibility
      let displayAmount;
      if (services[i].service_amount !== undefined && services[i].service_amount !== null) {
        // Use stored calculated value (preferred)
        const serviceAmount = parseFloat(services[i].service_amount || 0);
        const serviceDiscount = parseFloat(services[i].service_discount || 0);
        displayAmount = this.formatCurrency(serviceAmount - serviceDiscount);
      } else {
        // Fallback calculation for older records that might not have service_value
        const serviceAmount = parseFloat(services[i].service_amount || 0);
        const serviceDiscount = parseFloat(services[i].service_discount || 0);
        displayAmount = this.formatCurrency(serviceAmount - serviceDiscount);
      }

      // Format materials for display
      let materialsDisplay = '-';
      if (services[i].materials && services[i].materials.length > 0) {
        if (typeof services[i].materials[0] === 'object') {
          materialsDisplay = services[i].materials
            .map(m => m.brand ? `${m.brand} - ${m.product_name}` : m.product_name)
            .join(', ');
        } else {
          materialsDisplay = Array.isArray(services[i].materials)
            ? services[i].materials.join(', ')
            : services[i].materials;
        }
      }

      // Format recommended product for display
      let recommendedProductDisplay = null;
      if (services[i].recommended_product_id && services[i].recommended_product_name) {
        recommendedProductDisplay = services[i].recommended_product_brand
          ? `${services[i].recommended_product_brand} - ${services[i].recommended_product_name}`
          : services[i].recommended_product_name;
      }

      // Check if this service has actual materials collected
      const serviceHasMaterials = services[i].materials && services[i].materials.length > 0;

      // Build combined product display with appropriate icon
      let productDisplay = '-';
      if (serviceHasMaterials && materialsDisplay !== '-') {
        // Actual materials used - cubes icon
        productDisplay = `<span class="text-info"><i class="fa-sharp fa-regular fa-cubes mr-1"></i><strong>Used:</strong> ${materialsDisplay}</span>`;
      } else if (recommendedProductDisplay) {
        // Recommended product - checkmark icon
        productDisplay = `<span class="text-success"><i class="fa-sharp fa-regular fa-check mr-1"></i><strong>Use:</strong> ${recommendedProductDisplay}</span>`;
      }

      // Quantity-aware display
      const qty = parseInt(services[i].quantity, 10) || 1;
      const unitPrice = parseFloat(services[i].unit_price || 0);
      let descText = services[i].custom_description;
      if (qty > 1 && unitPrice > 0) {
        descText = `${descText}  x${qty} @ $${unitPrice.toFixed(2)}`;
      }

      const catPrefix = services[i].service_category ? `${services[i].service_category} - ` : '';
      let rowView = {
        serviceId: services[i].id || services[i].service_id,
        desc: `${catPrefix}${descText}`,
        productDisplay: productDisplay,
        amount: displayAmount,
        duration: services[i].service_duration
      };

      services_.push(rowView);
    }

    // Determine which invoice number to display
    const invoiceDisplay = this.props.request.customer_facing_invoice 
      ? this.props.request.customer_facing_invoice 
      : this.props.request.invoiceId;

    return(
      <Modal
        show={this.props.show}
        onHide={this.props.onHide}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Work Detail for {this.props.request.dealerName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col">
              <h6>Email: {this.props.request.email}</h6>
            </div>
            <div className="col">
              <h6>Phone: {this.props.request.phone_number}</h6>
            </div>
          </div>
          <hr></hr>
          <div className="row">
            <div className="col text-center">
              <h5>{vehicle}</h5>
            </div>
            {(!this.props.request.vin || this.props.request.vin.substring(0, 2) !== "NV") && (
              <div className="col text-center">
                <h5>
                  VIN :{" "}
                  <span
                    onClick={() => this.copyToClipboard(this.props.request.vin, 'vin')}
                    style={{ cursor: 'pointer', borderBottom: '1px dashed #6c757d', position: 'relative' }}
                    title="Click to copy VIN"
                  >
                    {this.state.copiedField === 'vin' && (
                      <span style={{ position: 'absolute', top: '-1.5em', left: '50%', transform: 'translateX(-50%)', fontSize: '0.7rem', whiteSpace: 'nowrap', color: '#28a745' }}>
                        Copied to Clipboard
                      </span>
                    )}
                    {this.props.request.vin || "N/A"}
                    <i className="fa-sharp fa-regular fa-copy text-muted ml-2" style={{ fontSize: '0.8rem' }}></i>
                  </span>
                </h5>
              </div>
            )}
          </div>
          <hr></hr>
          <div className="row">
            <div className="col">
              <h6>{startLabel} : {startDate}</h6>
            </div>
            <div className="col">
              <h6>{endLabel} : {endDate}</h6>
            </div>
          </div>
          <hr></hr>
          
          {/* Enhanced pricing display with stored values */}
          {this.props.request.requestType === "installer" && (
            <div>
              <div className="row">
                <div className="col text-center">
                  <h5>Total Cost : {totalCost} including taxes of {totalTax}</h5>
                </div>
              </div>
              {requestDiscount > 0 && (
                <div className="row">
                  <div className="col text-center">
                    <small className="text-muted">
                      Original: {this.formatCurrency(requestTotal)} - 
                      Discount: {this.formatCurrency(requestDiscount)} = 
                      Subtotal: {this.formatCurrency(finalAmount)}
                    </small>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {this.props.request.requestType === "dealer" && (
            <div>
              <div className="row">
                <div className="col text-center">
                  <h5>Total Cost : {this.formatCurrency(finalAmount)}</h5>
                </div>
              </div>
              {requestDiscount > 0 && (
                <div className="row">
                  <div className="col text-center">
                    <small className="text-muted">
                      Original: {this.formatCurrency(requestTotal)} - 
                      Discount: {this.formatCurrency(requestDiscount)}
                    </small>
                  </div>
                </div>
              )}
            </div>
          )}

          {noteText !== "None" && (
            <div>
              <hr></hr>
              <div className="row">
                <div className="col">
                  <p>Work Notes : {this.props.notes[0].note_text}</p>
                </div>
              </div>
            </div>
          )}
          <hr></hr>
          <h5>Services</h5>
          <BootstrapTable
            keyField="serviceId"
            data={services_}
            columns={columns}
          />
          
          {/* Show breakdown of stored values for transparency */}
          <div className="row mt-3">
            <div className="col-12">
              <small className="text-muted">
                <div>Original Total: {this.formatCurrency(requestTotal)}</div>
                {requestDiscount > 0 && (
                  <div>Total Discount: {this.formatCurrency(requestDiscount)}</div>
                )}
                <div>Subtotal: {this.formatCurrency(finalAmount)}</div>
                {requestTaxes > 0 && (
                  <div>Taxes: {this.formatCurrency(requestTaxes)}</div>
                )}
                <div><strong>Customer Pays: {totalCost}</strong></div>
              </small>
            </div>
          </div>

          <div className="row">
            <div className="col">
              <p>Request # {this.props.request.requestId}</p>
            </div>
            <div className="col">
              <p>
                Invoice #{" "}
                {invoiceDisplay ? (
                  <span
                    onClick={() => this.copyToClipboard(invoiceDisplay, 'invoice')}
                    style={{ cursor: 'pointer', borderBottom: '1px dashed #6c757d', position: 'relative' }}
                    title="Click to copy Invoice #"
                  >
                    {this.state.copiedField === 'invoice' && (
                      <span style={{ position: 'absolute', top: '-1.5em', left: '50%', transform: 'translateX(-50%)', fontSize: '0.7rem', whiteSpace: 'nowrap', color: '#28a745' }}>
                        Copied to Clipboard
                      </span>
                    )}
                    {invoiceDisplay}
                    <i className="fa-sharp fa-regular fa-copy text-muted ml-2" style={{ fontSize: '0.8rem' }}></i>
                  </span>
                ) : (
                  <span>N/A</span>
                )}
              </p>
            </div>
            <div className="col">
              <p>
                Current State : {_.capitalize(this.props.request.currentState)}
              </p>
            </div>
          </div>

          {/* State History */}
          {this.props.history && this.props.history.length > 0 && (
            <div className="row mt-3">
              <div className="col-12">
                <h6>State History</h6>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Changed By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.props.history.slice().reverse().map((state, index) => (
                        <tr key={index}>
                          <td>{convertFromUTC(state.stateDate).format("MMM D, YYYY h:mm A")}</td>
                          <td>{_.capitalize(state.requestStatus)}</td>
                          <td>{state.changedByUserEmail || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <div className="row">
            <div className="col">
              {this.props.request.contract_url && (
                <button
                  type="button"
                  onClick={this.fetchSignedUrl}
                  className="button btn-secondary m-2 buttons"
                >
                  CUSTOMER CONTRACT
                </button>
              )}
              {(this.props.request.quoteSentAt || this.props.request.quote_sent_at) && (
                <button
                  type="button"
                  onClick={this.fetchEstimatePdf}
                  className="button btn-secondary m-2 buttons"
                >
                  ESTIMATE PDF
                </button>
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }
}